import { useCallback, useEffect, useRef, useState, type CSSProperties, type KeyboardEvent, type PointerEvent, type ReactNode } from "react";
import { LuBox, LuCheck, LuChevronLeft, LuChevronRight, LuCrosshair, LuPlay, LuTarget } from "react-icons/lu";
import { coachSessions, frameSrc, frameSrcSet, type SessionId } from "./coach";
import "./coach.css";

const sessionIcon: Record<SessionId, ReactNode> = { cs2: <LuCrosshair />, blender: <LuBox /> };
const ADVANCE_MS = 5600;

const clock = (total: number) => `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;

/**
 * The desktop app's coach review, rebuilt live: the evidence frame with a ring on the moment, a scrubbable timeline
 * with numbered moments, the coach summary and moment list, what happened / what a pro would do / why, and the drill.
 * "page" is the standalone showcase; "app" sits inside the AppShowcase window at desktop-app density.
 */
export function CoachReview({ initial = "cs2", locked = false, variant = "page" }: { initial?: SessionId; locked?: boolean; variant?: "page" | "app" }) {
  const [id, setId] = useState<SessionId>(initial);
  const [index, setIndex] = useState(variant === "app" ? 1 : 0);
  const [auto, setAuto] = useState(true);
  const [scrub, setScrub] = useState<number | null>(null);
  const [cycle, setCycle] = useState(0);
  const root = useRef<HTMLDivElement>(null);
  const track = useRef<HTMLDivElement>(null);
  const session = coachSessions[id];
  const moment = session.moments[index]!;
  const count = session.moments.length;
  const pct = (seconds: number) => (seconds / session.seconds) * 100;

  // Advance through the moments while the review is on screen, until the visitor takes over.
  useEffect(() => {
    if (!auto || variant === "app" || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let visible = false;
    const observer = new IntersectionObserver(([entry]) => { visible = !!entry?.isIntersecting; root.current?.classList.toggle("cr-live", visible); }, { threshold: .35 });
    if (root.current) observer.observe(root.current);
    const timer = window.setInterval(() => { if (visible) { setIndex(i => (i + 1) % count); setCycle(c => c + 1); } }, ADVANCE_MS);
    return () => { observer.disconnect(); window.clearInterval(timer); };
  }, [auto, count, variant]);

  const choose = useCallback((i: number) => { setAuto(false); setIndex(((i % count) + count) % count); }, [count]);
  const pick = (next: SessionId) => { setId(next); setIndex(0); setAuto(true); setCycle(c => c + 1); };

  // Scrubbing: drag along the track and the nearest moment follows the playhead.
  const nearest = (seconds: number) => session.moments.reduce((best, m, i) => Math.abs(m.at - seconds) < Math.abs(session.moments[best]!.at - seconds) ? i : best, 0);
  const seek = (event: PointerEvent<HTMLDivElement>) => {
    const box = track.current?.getBoundingClientRect();
    if (!box) return;
    const ratio = Math.min(1, Math.max(0, (event.clientX - box.left) / box.width));
    setScrub(ratio * 100);
    const i = nearest(ratio * session.seconds);
    if (i !== index) { setAuto(false); setIndex(i); }
  };
  const onKey = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "ArrowRight" || event.key === "ArrowUp") { event.preventDefault(); choose(index + 1); }
    if (event.key === "ArrowLeft" || event.key === "ArrowDown") { event.preventDefault(); choose(index - 1); }
    if (event.key === "Home") { event.preventDefault(); choose(0); }
    if (event.key === "End") { event.preventDefault(); choose(count - 1); }
  };

  const head = scrub ?? pct(moment.at);
  const calloutLeft = moment.x > .58;
  const calloutBelow = moment.y < .3;
  const frames = [...new Set(session.moments.map(m => m.frame))];

  return <div className={`cr cr-${variant}`} ref={root}>
    {!locked && variant === "page" && <div className="cr-tabs" role="group" aria-label="Choose a sample session">{(Object.keys(coachSessions) as SessionId[]).map(key => <button type="button" key={key} aria-pressed={id === key} onClick={() => pick(key)}>{sessionIcon[key]}<span><strong>{coachSessions[key].title}</strong><small>{coachSessions[key].app} · {coachSessions[key].length}</small><em className="cr-tab-short">{coachSessions[key].app}</em></span></button>)}</div>}

    <div className={variant === "page" ? "hs-window cr-window" : "cr-window"}>
      {variant === "page" && <div className="hs-window-bar"><span className="hs-dots" aria-hidden="true"><i /><i /><i /></span><span className="hs-window-title">Coach review · {session.title}</span><span className="cr-reviewed"><i />Reviewed</span></div>}

      <div className="cr-body">
        <div className="cr-main">
          <div className="cr-frame" style={{ "--x": moment.x, "--y": moment.y } as CSSProperties}>
            {frames.map(frame => <img key={frame} className={frame === moment.frame ? "on" : ""} src={frameSrc(frame)} srcSet={frameSrcSet(frame)} sizes={variant === "app" ? "(max-width: 760px) 92vw, 600px" : "(max-width: 760px) 92vw, (max-width: 1180px) 64vw, 760px"} width={1280} height={720} alt={frame === moment.frame ? `${session.app} session frame at ${moment.time}` : ""} loading="lazy" decoding="async" />)}
            <span className="cr-ring" aria-hidden="true"><span className="cr-ring-pulse" /></span>
            <div className={`cr-callout ${calloutLeft ? "left" : ""} ${calloutBelow ? "below" : ""}`} key={`${id}-${index}`} aria-hidden="true"><span className="cr-callout-meta"><b>{moment.n}</b>{moment.time}</span><span className="cr-callout-title">{moment.title}</span></div>
            <span className="cr-tc" aria-hidden="true">{scrub === null ? moment.time : clock(Math.round((scrub / 100) * session.seconds))} / {session.length}</span>
          </div>

          <div className="cr-scrub">
            <div className="cr-controls">
              <button type="button" className="cr-play" onClick={() => { setAuto(true); setCycle(c => c + 1); }} aria-label={`Play from ${moment.time}`}><LuPlay aria-hidden="true" /><span>Play<span className="cr-count-word"> from</span> {moment.time}</span></button>
              <button type="button" className="cr-step" onClick={() => choose(index - 1)} aria-label="Previous moment"><LuChevronLeft /></button>
              <button type="button" className="cr-step" onClick={() => choose(index + 1)} aria-label="Next moment"><LuChevronRight /></button>
              <span className="cr-count" aria-live="polite"><span className="cr-count-word">Moment </span>{index + 1} of {count}{auto && variant === "page" && <i key={cycle} className="cr-timer" style={{ animationDuration: `${ADVANCE_MS}ms` }} aria-hidden="true" />}</span>
              <span className="cr-clip">{moment.clip} clip</span>
            </div>
            <div className="cr-track" ref={track} role="slider" tabIndex={0} aria-label="Session timeline" aria-valuemin={1} aria-valuemax={count} aria-valuenow={index + 1} aria-valuetext={`Moment ${moment.n} at ${moment.time}: ${moment.title}`}
              onKeyDown={onKey}
              onPointerDown={event => { event.currentTarget.setPointerCapture(event.pointerId); seek(event); }}
              onPointerMove={event => { if (event.currentTarget.hasPointerCapture(event.pointerId)) seek(event); }}
              onPointerUp={() => setScrub(null)} onPointerCancel={() => setScrub(null)}>
              <span className="cr-rail" aria-hidden="true"><span className="cr-fill" style={{ width: `${head}%` }} /></span>
              <span className={`cr-head ${scrub !== null ? "dragging" : ""}`} style={{ left: `${head}%` }} aria-hidden="true" />
              {session.moments.map((m, i) => <button type="button" tabIndex={-1} key={m.n} className={`cr-mark ${i === index ? "on" : ""} ${i < index ? "past" : ""}`} style={{ left: `${pct(m.at)}%` }} onPointerDown={event => event.stopPropagation()} onClick={() => choose(i)} aria-label={`Moment ${m.n} at ${m.time}: ${m.title}`}>{m.n}</button>)}
            </div>
            <div className="cr-ticks" aria-hidden="true">{[0, .25, .5, .75, 1].map(t => <span key={t}>{clock(Math.round(session.seconds * t))}</span>)}</div>
          </div>
        </div>

        <aside className="cr-side">
          <span className="cr-kicker">Coach review</span>
          <h3>{session.headline}</h3>
          {variant === "page" && <p className="cr-summary">{session.summary}</p>}
          <ol className="cr-list">{session.moments.map((m, i) => <li key={m.n}><button type="button" className={i === index ? "on" : ""} aria-current={i === index ? "true" : undefined} onClick={() => choose(i)}><b>{m.n}</b><span><strong>{m.title}</strong><small>{m.category}</small></span><time>{m.time}</time></button></li>)}</ol>
        </aside>
      </div>

      <div className="cr-detail">
        <div className="cr-notes" key={`${id}-${index}`}>
          <div><span>What happened</span><p>{moment.happened}</p></div>
          <div className="pro"><span>What a pro would do</span><p>{moment.pro}</p></div>
          <div><span>Why it matters</span><p>{moment.why}</p></div>
        </div>
        <div className="cr-drill">
          <div className="cr-drill-head"><span><LuTarget aria-hidden="true" /> One drill for next time</span><em>{session.drill.minutes} min</em></div>
          <h4>{session.drill.title}</h4>
          {variant === "page" && <p>{session.drill.body}</p>}
          <p className="cr-check"><LuCheck aria-hidden="true" />{session.drill.check}</p>
        </div>
      </div>
    </div>
    {variant === "page" && <p className="hs-footnote">A sample review from the Hindset app, written for this session to show the format. Automatic reviews are in development.</p>}
  </div>;
}
